<template>
  <el-dialog
    class="dialog-wrapper"
    :visible="true"
    :show-close="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('specifications.update.updateSpecification')
          : $t('specifications.create.createSpecification')
      "
    />
    <ValidationObserver
      ref="specificationForm"
      v-slot="{ handleSubmit, invalid, dirty }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          v-if="!isModalEdit"
          :gutter="20"
          type="flex"
          class="specification-type-row"
        >
          <el-col :span="24">
            <FormItem :label="$t('specifications.specificationTypes.title')">
              <el-radio-group v-model="specificationType">
                <el-tooltip
                  :disabled="!hasStandardSpecification"
                  :content="
                    $t(
                      'specifications.specificationTypes.standardDisabledTooltip'
                    )
                  "
                  placement="top"
                  effect="light"
                >
                  <el-radio
                    label="standard"
                    :disabled="hasStandardSpecification"
                  >
                    {{ $t('specifications.specificationTypes.standard') }}
                  </el-radio>
                </el-tooltip>
                <el-radio label="custom">
                  {{ $t('specifications.specificationTypes.custom') }}
                </el-radio>
              </el-radio-group>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          v-if="specificationType === 'custom' || isModalEdit"
          :gutter="20"
          type="flex"
        >
          <el-col :span="8">
            <FormItem
              :label="$t('specifications.create.customer')"
              rules="required"
            >
              <el-autocomplete
                v-model="selectedCustomer"
                style="width: 100%"
                :fetch-suggestions="customerAutocomplete"
                value-key="label"
                placeholder=""
                clearable
                @select="
                  (item) => {
                    formData.customerId = item.value;
                  }
                "
                @change="
                  () => {
                    formData.customerId = null;
                  }
                "
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem :label="$t('specifications.create.customerProductCode')">
              <el-input v-model="formData.customerProductCode" />
            </FormItem>
          </el-col>
        </el-row>

        <FormSpecifications
          :form-value="formData"
          :measure="selectedMeasure"
        />

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortAction"
          >
            {{ $t('specifications.create.abortAction') }}
          </el-button>
          <el-button
            v-if="isModalEdit"
            type="primary"
            native-type="submit"
            :disabled="invalid || !dirty"
            @click.prevent="handleSubmit(patchSpecification)"
          >
            {{ $t('specifications.update.updateSpecification') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            :disabled="invalid || !dirty"
            @click.prevent="handleSubmit(createSpecification)"
          >
            {{ $t('specifications.create.createSpecification') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { mapGetters } from 'vuex';
  import pickBy from 'lodash.pickby';

  import { analysesApi, customersApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import FormSpecifications from '@/components/FormSpecifications.vue';

  const defaultData = {
    customerId: null,
    customerProductCode: null,
    operationRegistryId: null,
    type: 'None',
    selectionValues: [],
    decimalDigits: null,
    minRange: null,
    maxRange: null,
    isMinRangeInclusive: null,
    isMaxRangeInclusive: null,
    expectedValue: null,
    specificationValue: null,
    recoveryFactor: null,
  };

  export default {
    name: 'CreateEditSpecification',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      AutocompleteItem,
      FormSpecifications,
    },
    props: {
      isModalEdit: { type: Boolean, default: false },
      initialData: { type: Object, default: () => defaultData },
      hasStandardSpecification: { type: Boolean, default: false },
    },
    data() {
      return {
        formData: { ...defaultData },
        selectedMeasure: null,
        selectedCustomer: '',
        specificationType: 'standard',
      };
    },
    computed: {
      ...mapGetters(['forms']),
    },
    async created() {
      try {
        if (this.isModalEdit) {
          const { description, customerId } = this.initialData;
          this.selectedMeasure = description || '';
          // Determine specification type based on customerId presence
          this.specificationType = customerId ? 'custom' : 'standard';
          if (customerId) {
            const response = await customersApi.autocompleteFetch(
              'name',
              customerId,
              false,
              true
            );
            this.selectedCustomer = response.data.length
              ? `${response.data[0].id} ${response.data[0].name}`
              : '';
          }
          this.formData = {
            ...this.initialData,
          };
        } else {
          const { description } = this.initialData;
          this.selectedMeasure = description || '';
          // Default to standard if available, otherwise custom
          this.specificationType = this.hasStandardSpecification
            ? 'custom'
            : 'standard';
        }
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      }
    },
    methods: {
      checkFieldsByLimitType(limitType, payload) {
        let formattedPayload = {};
        switch (limitType) {
          case 'None': {
            formattedPayload = {};
            break;
          }
          case 'FreeText': {
            formattedPayload = {
              specificationValue: payload.specificationValue,
              customerId: payload.customerId,
              customerProductCode: payload.customerProductCode,
            };
            break;
          }
          case 'Selection': {
            formattedPayload = {
              selectionValues: payload.selectionValues,
              specificationValue: payload.specificationValue,
              customerId: payload.customerId,
              customerProductCode: payload.customerProductCode,
            };
            break;
          }
          case 'Range': {
            formattedPayload = {
              decimalDigits: payload.decimalDigits,
              minRange: payload.minRange,
              maxRange: payload.maxRange,
              isMinRangeInclusive: payload.isMinRangeInclusive,
              isMaxRangeInclusive: payload.isMaxRangeInclusive,
              customerId: payload.customerId,
              customerProductCode: payload.customerProductCode,
            };
            break;
          }
          default:
            formattedPayload = payload;
            break;
        }
        formattedPayload.type = payload.type;
        return formattedPayload;
      },
      async createSpecification() {
        const formData = this.buildFormToSend();
        // Clear customer fields for standard specifications
        if (this.specificationType === 'standard') {
          delete formData.customerId;
          delete formData.customerProductCode;
        }
        const payload = this.checkFieldsByLimitType(formData.type, formData);
        try {
          await analysesApi.specificationCreate(
            this.initialData.analysisId,
            this.initialData.operationRegistryId,
            payload
          );
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('specifications.create.success'),
          });
          this.formData = { ...defaultData };
          this.$refs.specificationForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async patchSpecification() {
        const formData = this.buildFormToSend();
        const payload = this.checkFieldsByLimitType(formData.type, formData);
        try {
          await analysesApi.specificationPatch(
            this.initialData.analysisId,
            this.initialData.operationRegistryId,
            payload,
            this.initialData.id
          );
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('specifications.update.success'),
          });
          this.formData = { ...defaultData };
          this.$refs.specificationForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      parseDecimal(value) {
        if (value == null || value === '') return null;
        return parseFloat(String(value).replace(',', '.'));
      },
      buildFormToSend() {
        const payload = {
          ...this.formData,
          recoveryFactor: this.parseDecimal(this.formData.recoveryFactor),
        };
        return pickBy(payload, (x) => x !== null);
      },
      abortAction() {
        this.formData = { ...defaultData };
        this.selectedCustomer = null;
        this.$refs.specificationForm.reset();
        this.$emit('dialogClose', { success: false });
      },
      async customerAutocomplete(value, cb) {
        if (value && value.length >= 3) {
          const response = await customersApi.autocompleteFetch(
            'name',
            value,
            false,
            true
          );
          const customers = response.data.map((c) => ({
            value: c.id,
            label: `${c.id} ${c.name}`,
          }));
          cb(customers);
        } else {
          cb([]);
        }
      },
    },
  };
</script>

<style lang="scss">
  .el-radio-group {
    width: 100%;
  }

  .add-btn {
    margin-bottom: 20px;
  }

  .el-icon-error {
    color: red;

    &:hover {
      cursor: pointer;
    }
  }

  .el-dialog {
    .el-form-item {
      margin-bottom: 0;
    }
  }

  .range-input {
    display: flex;

    .el-radio {
      margin-top: 2rem;
      margin-right: 8px;

      &__label {
        display: none;
      }
    }

    label {
      margin-right: 5px;
    }
  }

  .centered-col {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .spec-value {
    display: flex;

    .el-input {
      margin-right: 10px;
    }
  }
</style>
